import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Baby,
  CircleAlert,
  Copy,
  GraduationCap as UniIcon,
  GraduationCap,
  Loader2,
  Share2,
  Sparkles,
  Building2,
  Home,
  School,
} from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { SchoolsAroundReport } from '../../types/schoolsAround';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';

const SchoolsAroundDetailPage: React.FC = () => {
  const { neighborhoodSlug } = useParams<{ neighborhoodSlug: string }>();
  const [report, setReport] = useState<SchoolsAroundReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedSchool, setCopiedSchool] = useState<string | null>(null);

  useEffect(() => {
    const slug = String(neighborhoodSlug || '')
      .trim()
      .toLowerCase();
    if (!slug) {
      setError('Missing neighborhood slug.');
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getSchoolsAroundReport?slug=${encodeURIComponent(slug)}`
        );
        const result = await response.json();
        if (!result.success || !result.data) {
          setError('Schools page not found for this neighborhood.');
          return;
        }
        setReport(result.data as SchoolsAroundReport);
      } catch (e) {
        setError('Unable to load this schools page right now.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [neighborhoodSlug]);

  const mergedFaq = useMemo(() => {
    if (!report) return [];
    const seen = new Set<string>();
    return [...report.seo_content.faq, ...(report.ai_enrichment?.extra_faq || [])].filter(
      (item) => {
        const question = String(item?.question || '').trim();
        const answer = String(item?.answer || '').trim();
        if (!question || !answer) return false;

        const key = question.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }
    );
  }, [report]);

  const schoolGroups = useMemo(() => {
    if (!report) return [];

    const schools = report.schools_by_category || {
      primary_schools: [],
      creches: [],
      secondary_schools: [],
      universities: [],
    };

    return [
      {
        key: 'primary',
        title: 'Primary Schools',
        subtitle: 'Early and primary years',
        icon: School,
        items: schools.primary_schools || [],
        className: 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white',
        chipClass: 'bg-emerald-100 text-emerald-700',
      },
      {
        key: 'creches',
        title: 'Creches & Preschool',
        subtitle: 'Childcare and pre-school options',
        icon: Baby,
        items: schools.creches || [],
        className: 'border-cyan-200 bg-gradient-to-br from-cyan-50 to-white',
        chipClass: 'bg-cyan-100 text-cyan-700',
      },
      {
        key: 'secondary',
        title: 'Secondary Schools',
        subtitle: 'Post-primary options',
        icon: GraduationCap,
        items: schools.secondary_schools || [],
        className: 'border-violet-200 bg-gradient-to-br from-violet-50 to-white',
        chipClass: 'bg-violet-100 text-violet-700',
      },
      {
        key: 'university',
        title: 'Universities & Colleges',
        subtitle: 'Third-level institutions nearby',
        icon: UniIcon,
        items: schools.universities || [],
        className: 'border-amber-200 bg-gradient-to-br from-amber-50 to-white',
        chipClass: 'bg-amber-100 text-amber-700',
      },
    ];
  }, [report]);

  const totalSchoolCount = useMemo(
    () => schoolGroups.reduce((sum, group) => sum + group.items.length, 0),
    [schoolGroups]
  );

  const copySocialSnippet = async (snippet: string, index: number) => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1400);
    } catch {
      // no-op
    }
  };

  const copySchoolInfo = async (school: any, groupKey: string, schoolIndex: number) => {
    const schoolText = `${school.name}${school.address ? ` - ${school.address}` : ''} (${school.distance_km.toFixed(1)} km away)\n\nFind the best properties in Ireland quickly on RentSmart.ie`;
    try {
      await navigator.clipboard.writeText(schoolText);
      setCopiedSchool(`${groupKey}-${schoolIndex}`);
      setTimeout(() => setCopiedSchool(null), 1400);
    } catch {
      // no-op
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-cyan-600 mb-3" />
        <p className="font-bold text-slate-500">Loading schools insights...</p>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="max-w-2xl mx-auto my-20 px-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center">
          <CircleAlert className="h-16 w-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black text-slate-900 mb-3">Page Unavailable</h1>
          <p className="text-slate-600 mb-8">{error || 'Schools content unavailable.'}</p>
          <Link
            to="/schools-around"
            className="inline-block rounded-2xl bg-cyan-700 text-white px-8 py-4 font-black hover:bg-cyan-800 no-underline"
          >
            Back to Schools Hub
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://rentsmart.ie/schools-around/${report.slug}`;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const shareTitle = `Schools around ${report.neighborhood_name}, ${report.county_name}`;
  const shareText =
    report.ai_enrichment?.social_share_snippets?.[0] ||
    `Planning a move to ${report.neighborhood_name}? See school-access insights and search homes in one flow on RentSmart.ie.`;
  const rentBrowseHref = `/browse?listingIntent=rent&location=${encodeURIComponent(
    report.neighborhood_name || 'dublin'
  )}`;
  const saleBrowseHref = `/browse?listingIntent=sale&location=${encodeURIComponent(
    report.neighborhood_name || 'dublin'
  )}`;

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>{report.seo_content.meta_title}</title>
        <meta name="description" content={report.seo_content.meta_desc} />
        <meta name="keywords" content={report.seo_content.keywords.join(', ')} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={report.seo_content.meta_title} />
        <meta property="og:description" content={report.seo_content.meta_desc} />
        <meta property="og:url" content={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(report.schema)}</script>
      </Helmet>

      <header className="bg-slate-900 text-white pt-12 pb-16 px-4 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute -bottom-16 -left-8 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-4">
            <GraduationCap className="h-4 w-4" /> Schools & Neighborhood Fit
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
            {report.seo_content.h1}
          </h1>
          <p className="text-slate-300 max-w-4xl text-lg leading-relaxed">
            {report.seo_content.snippet_hook}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-slate-400">
              <Share2 className="h-3.5 w-3.5" /> Share
            </span>
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={30} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareText}>
              <TwitterIcon size={30} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={30} round />
            </WhatsappShareButton>
            <LinkedinShareButton
              url={shareUrl}
              title={shareTitle}
              summary={shareText}
              source="RentSmart.ie"
            >
              <LinkedinIcon size={30} round />
            </LinkedinShareButton>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 -mt-8 pb-16">
        <section className="grid lg:grid-cols-12 gap-8 mt-16">
          <div className="lg:col-span-8 space-y-8">
            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">School Access Snapshot</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    Access Score
                  </p>
                  <p className="text-2xl font-black text-slate-900">
                    {report.school_access_signals.school_access_score}/100
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    Local Authority
                  </p>
                  <p className="text-sm font-black text-slate-800">{report.local_authority}</p>
                </div>
                <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3">
                  <p className="text-[10px] uppercase tracking-wider font-black text-cyan-700 mb-1">
                    Schools Indexed
                  </p>
                  <p className="text-2xl font-black text-cyan-900">{totalSchoolCount}</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                Signal values are directional planning aids and not official enrollment counts.
              </p>
            </div>

            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-2">
                Schools Around {report.neighborhood_name}
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-5">
                Browse nearby schools by category, then move straight to homes in the same area.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {schoolGroups.map((group) => {
                  const GroupIcon = group.icon;
                  return (
                    <div
                      key={group.key}
                      className={`rounded-2xl border p-4 shadow-sm ${group.className}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <div className="inline-flex items-center gap-2 text-slate-900">
                            <GroupIcon className="h-4 w-4" />
                            <p className="text-sm font-black uppercase tracking-wider">
                              {group.title}
                            </p>
                          </div>
                          <p className="text-xs text-slate-600 mt-1 font-semibold">
                            {group.subtitle}
                          </p>
                        </div>
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${group.chipClass}`}
                        >
                          {group.items.length} found
                        </span>
                      </div>

                      {group.items.length ? (
                        <ul className="space-y-2">
                          {group.items.map((school, idx) => (
                            <li
                              key={`${school.name}-${idx}`}
                              className="rounded-xl border border-white/70 bg-white/90 px-3 py-2 flex items-start justify-between gap-2"
                            >
                              <div className="flex-1">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="text-sm font-bold text-slate-800 leading-tight">
                                    {school.name}
                                  </p>
                                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 whitespace-nowrap">
                                    {school.distance_km.toFixed(1)} km
                                  </span>
                                </div>
                                {school.address ? (
                                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                                    {school.address}
                                  </p>
                                ) : null}
                              </div>
                              <button
                                type="button"
                                onClick={() => copySchoolInfo(school, group.key, idx)}
                                className={`flex-shrink-0 rounded-lg border p-2 transition-colors ${copiedSchool === `${group.key}-${idx}` ? 'border-emerald-300 bg-emerald-100 text-emerald-700' : 'border-slate-200 bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                                title="Copy school details"
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="rounded-xl border border-dashed border-slate-300 bg-white/70 px-3 py-4">
                          <p className="text-xs font-semibold text-slate-600 leading-relaxed">
                            No schools indexed yet for this category in this area.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {report.ai_enrichment?.school_highlights?.length ? (
              <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="h-5 w-5 text-violet-600" />
                  <h2 className="text-2xl font-black text-slate-900">School Highlights</h2>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-5">
                  Helpful context about notable schools in {report.neighborhood_name}.
                </p>
                <div className="space-y-4">
                  {report.ai_enrichment.school_highlights.map((highlight, idx) => {
                    const categoryColors = {
                      primary: 'border-emerald-200 bg-emerald-50 text-emerald-700',
                      creche: 'border-cyan-200 bg-cyan-50 text-cyan-700',
                      secondary: 'border-violet-200 bg-violet-50 text-violet-700',
                      university: 'border-amber-200 bg-amber-50 text-amber-700',
                    };
                    const colorClass =
                      categoryColors[highlight.category as keyof typeof categoryColors] ||
                      'border-slate-200 bg-slate-50 text-slate-700';

                    return (
                      <div
                        key={`${highlight.school_name}-${idx}`}
                        className="rounded-2xl border border-slate-200 bg-white p-5"
                      >
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <h3 className="text-lg font-black text-slate-900">
                            {highlight.school_name}
                          </h3>
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${colorClass}`}
                          >
                            {highlight.category}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">
                          {highlight.brief_info}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {report.ai_enrichment ? (
              <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
                <h2 className="text-2xl font-black text-slate-900 mb-4">Family Guidance</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  {report.ai_enrichment.family_fit_summary}
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-cyan-200 bg-cyan-50 p-4">
                    <p className="text-xs uppercase tracking-wider font-black text-cyan-700 mb-2">
                      School Selection Playbook
                    </p>
                    <ul className="space-y-2 text-sm text-cyan-900 font-semibold">
                      {report.ai_enrichment.school_selection_playbook.map((item, idx) => (
                        <li key={`${item}-${idx}`}>
                          {idx + 1}. {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                    <p className="text-xs uppercase tracking-wider font-black text-rose-700 mb-2">
                      Admissions Watchouts
                    </p>
                    <ul className="space-y-2 text-sm text-rose-900 font-semibold">
                      {report.ai_enrichment.admissions_watchouts.map((item, idx) => (
                        <li key={`${item}-${idx}`}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-wider font-black text-slate-500 mb-2">
                    Neighborhood Education Signal
                  </p>
                  <p className="text-sm text-slate-700 font-semibold leading-relaxed">
                    {report.ai_enrichment.local_education_signal}
                  </p>
                </div>
                <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-xs uppercase tracking-wider font-black text-emerald-700 mb-2">
                    Family Vibe
                  </p>
                  <p className="text-sm text-emerald-900 font-semibold leading-relaxed">
                    {report.ai_enrichment.neighborhood_school_vibe}
                  </p>
                </div>
              </div>
            ) : null}

            {report.ai_enrichment?.social_share_snippets?.length ? (
              <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-5 w-5 text-cyan-600" />
                  <h2 className="text-2xl font-black text-slate-900">Social Share Snippets</h2>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {report.ai_enrichment.social_share_snippets.map((item, idx) => (
                    <div
                      key={`${item}-${idx}`}
                      className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                    >
                      <p className="text-sm text-slate-700 font-semibold leading-relaxed mb-3">
                        {item}
                      </p>
                      <button
                        type="button"
                        onClick={() => copySocialSnippet(item, idx)}
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-black uppercase tracking-wider ${
                          copiedIndex === idx
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                            : 'border-slate-300 bg-white text-slate-700'
                        }`}
                      >
                        <Copy className="h-3.5 w-3.5" /> {copiedIndex === idx ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <PseoEmailCapture
              pageType="schools-around"
              pageSlug={report.slug}
              locationName={report.neighborhood_name}
              contextHeadline={`School-Area Alerts For ${report.neighborhood_name}`}
              contextReason={`Receive Irish market updates tailored to families and movers tracking school-access areas like ${report.neighborhood_name}, including new local reports and property-market changes.`}
            />

            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">FAQ</h2>
              <div className="space-y-3">
                {mergedFaq.map((item, idx) => (
                  <details
                    key={`${item.question}-${idx}`}
                    className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <summary className="cursor-pointer font-bold text-slate-800">
                      {item.question}
                    </summary>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{item.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-8 space-y-6">
              <div className="rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-900 p-6 text-white shadow-xl">
                <h3 className="text-2xl font-black mb-2 flex items-center gap-2">
                  <Home className="h-5 w-5" /> Rentals in {report.neighborhood_name}
                </h3>
                <p className="text-sm text-emerald-100 mb-5 leading-relaxed">
                  Search active rental listings in the same neighborhood as your school shortlist.
                </p>
                <a
                  href={rentBrowseHref}
                  className="no-underline inline-flex w-full items-center justify-between rounded-xl bg-white px-4 py-3 font-black text-emerald-700"
                >
                  {report.ctas.rent.label} <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <div className="rounded-3xl bg-gradient-to-br from-sky-700 to-sky-900 p-6 text-white shadow-xl">
                <h3 className="text-2xl font-black mb-2 flex items-center gap-2">
                  <Building2 className="h-5 w-5" /> Homes for Sale
                </h3>
                <p className="text-sm text-sky-100 mb-5 leading-relaxed">
                  Compare purchase options in {report.neighborhood_name} after validating school
                  fit.
                </p>
                <a
                  href={saleBrowseHref}
                  className="no-underline inline-flex w-full items-center justify-between rounded-xl bg-white px-4 py-3 font-black text-sky-800"
                >
                  {report.ctas.buy.label} <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-6">
                <h3 className="text-xl font-black text-slate-900 mb-3">Area Context</h3>
                <Link
                  to={report.ctas.area_profile.path}
                  className="no-underline inline-flex w-full items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-black text-slate-700"
                >
                  {report.ctas.area_profile.label} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              <Link
                to="/schools-around"
                className="no-underline inline-flex w-full items-center justify-center rounded-xl border-2 border-dashed border-slate-300 px-4 py-3 text-xs font-black uppercase tracking-wider text-slate-500 hover:border-cyan-500 hover:text-cyan-700"
              >
                Explore Other Neighborhoods
              </Link>
            </div>
          </aside>
        </section>

        <section className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <p className="text-sm text-amber-900/90 font-semibold leading-relaxed">
            This report is informational and directional only. RentSmart does not provide legal,
            admissions, financial, or investment advice. Confirm school placement details with
            official school and authority sources.
          </p>
        </section>
      </main>
    </div>
  );
};

export default SchoolsAroundDetailPage;
